import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Stack,
  TextField
} from '@mui/material'
import React, { type FocusEvent, useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import Icon from 'components/Icon'
import { type AuthenticationFormValues } from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolPrivateNetworkIntegration } from 'components/status-checks/form/authentication/ObservabilityToolPrivateNetworkIntegration'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'

const fieldsToWatch = [
  'authentication.username',
  'authentication.password'
] as const

/**
 * Renders a `username` and `password` field required for creating a new Prometheus Authentication.
 * These fields are combined as `username:password` and are base64 encoded to form a Basic Auth token.
 */
export const ObservabilityToolPrometheusAuthenticationDetails = () => {
  const { isEdit } = useObservabilityToolAuthenticationContext()
  const { control, setValue, trigger } =
    useFormContext<AuthenticationFormValues>()

  const [username, password] = useWatch({
    control,
    name: fieldsToWatch
  })

  /*
   * Clear all authentication fields in the event that we even attempt to
   * update the value of even one of the fields. Check whether the value of the
   * field is equal to our placeholder string such that we don't continue to clear
   * the field after valid input has been entered.
   */
  function clearAuthenticationFields(
    _event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    isTouched: boolean
  ) {
    if (isTouched) return

    setValue('headers', [], { shouldDirty: true })
    setValue(
      'authentication',
      { username: '', password: '' },
      { shouldDirty: true, shouldTouch: true }
    )
  }

  /*
   * Given a valid username and password value, create an encoded string based on the
   * `username:password` combination to attach to the `headers` field as a Basic Auth token.
   */
  useEffect(() => {
    // re-evaluate the authentication fields if neither value is present. This should clear lingering errors.
    if (!username && !password) {
      trigger('authentication')
      return
    }

    // Encode nothing if either username or password are falsy, and reset the headers field state to an invalid value.
    if (!username || !password) {
      setValue('headers', [], { shouldDirty: true })

      return
    }

    const encodedApiKey = btoa(`${username}:${password}`)

    setValue(
      'headers',
      [{ key: 'Authentication', value: `Basic ${encodedApiKey}` }],
      { shouldDirty: true }
    )
  }, [username, password, setValue, trigger])

  return (
    <Stack spacing={2}>
      <FormControl>
        <Controller
          control={control}
          name="authenticationUrl"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              data-testid="observability-tool-authentication-api-base-url-input"
              disabled={isEdit}
              error={Boolean(error)}
              helperText={error?.message}
              label="Prometheus URL"
            />
          )}
        />
      </FormControl>
      <ObservabilityToolPrivateNetworkIntegration />
      <Accordion
        disableGutters
        data-testid="authentication-accordion"
        sx={{ maxWidth: 'breakpoints.values.sm' }}
      >
        <AccordionSummary expandIcon={<Icon icon="chevronDown" />}>
          Authentication (optional)
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2} sx={{ maxWidth: 400 }}>
            <FormControl>
              <Controller
                control={control}
                name="authentication.username"
                render={({ field, fieldState: { error, isTouched } }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    error={Boolean(error)}
                    helperText={error?.message}
                    label="Username"
                    type={isEdit ? 'password' : 'text'}
                    {...(isEdit && {
                      onFocus: (event) => {
                        clearAuthenticationFields(event, isTouched)
                      }
                    })}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <Controller
                control={control}
                name="authentication.password"
                render={({ field, fieldState: { error, isTouched } }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    error={Boolean(error)}
                    helperText={error?.message}
                    label="Password"
                    type="password"
                    {...(isEdit && {
                      onFocus: (event) => {
                        clearAuthenticationFields(event, isTouched)
                      }
                    })}
                  />
                )}
              />
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}
